/*
 * The video pop-up behind the play badges on the AIR carousel.
 *
 * Replo's runtime owned this: each slide container had an `openModal` onClick pointing at a
 * modal component that held a Vimeo embed. The conversion kept the pre-rendered HTML, which has
 * no modal in it at all, so the badges became artwork. The slide-to-video mapping recovered from
 * Replo's own page bundle lives in `snippets/replo-video-modal-map.liquid`; this file is the
 * behaviour.
 *
 * Slides are matched by `data-component-id`, the Replo component id the conversion preserved.
 * That matters for two reasons: the mapping cannot drift with slide order, and the carousel's
 * cloned slides (28 elements for 9 real slides) carry the id of the slide they clone, so they
 * open the right video without any extra work.
 *
 * Interaction copied from the Replo modal's own props: overlay #56607296, a 300x520 player
 * (280x500 below 641px), 14px corners, and a 30x30 close control top-right. Added on top of
 * that, because a modal that traps you is worse than no modal: Escape closes, a click on the
 * backdrop closes, focus moves to the close button and returns to the slide afterwards, and the
 * page behind is locked from scrolling while it is open (Replo did that with
 * `.replo-modal-after-open { overflow-y: hidden }`).
 *
 * ⚠ NAMED `replo-*`, NOT `pdp-*`, ON PURPOSE. The theme already ships an unrelated
 * `[data-pdp-video-modal]` - the PDP gallery's own modal, built around a native <video> with
 * pause/mute/progress controls. A first cut of this file used that same attribute name and a
 * probe then found the theme's hidden, empty overlay instead of this one, which reads exactly
 * like "the modal did not open". Keep this namespace clear of it.
 *
 * The iframe is created on open and destroyed on close. That is what stops playback - pausing a
 * cross-origin Vimeo player needs their JS API, and there is no reason to load it.
 */
(function () {
  'use strict';

  var OPEN_CLASS = 'replo-video-modal-open';
  var config = null;
  var open = null;          // { overlay, opener }

  function data() {
    if (config) return config;
    var el = document.getElementById('replo-video-modal-map');
    if (!el) return null;
    try { config = JSON.parse(el.textContent); } catch (e) { config = null; }
    return config;
  }

  function isMobile() {
    return window.matchMedia && window.matchMedia('(max-width: 640px)').matches;
  }

  function close() {
    if (!open) return;
    var opener = open.opener;
    // removing the iframe is what stops the video; there is no cross-origin pause without
    // Vimeo's own API loaded
    if (open.overlay.parentNode) open.overlay.parentNode.removeChild(open.overlay);
    document.documentElement.classList.remove(OPEN_CLASS);
    document.body.style.overflow = open.previousOverflow || '';
    open = null;
    if (opener && opener.focus) opener.focus();
  }

  function show(videoId, opener) {
    var cfg = data();
    if (!cfg || !videoId) return;
    close();

    var m = cfg.modal || {};
    var mobile = isMobile();
    var w = (mobile ? m.widthMobile : m.width) || 300;
    var h = (mobile ? m.heightMobile : m.height) || 520;
    var radius = (mobile ? m.radiusMobile : m.radius) || 14;

    var overlay = document.createElement('div');
    overlay.setAttribute('data-replo-video-modal', '');
    overlay.style.cssText = [
      'position:fixed', 'inset:0', 'z-index:2147483000',
      'background:' + (m.overlay || 'rgba(86, 96, 114, 0.588)'),
      'display:flex', 'align-items:center', 'justify-content:center', 'padding:16px'
    ].join(';');

    var frame = document.createElement('div');
    frame.style.cssText = [
      'position:relative', 'width:' + w + 'px', 'max-width:100%',
      'height:' + h + 'px', 'max-height:calc(100vh - 32px)',
      'border-radius:' + radius + 'px', 'overflow:hidden', 'background:#000'
    ].join(';');

    /*
     * A map value may be a bare id ("744275701") or Vimeo's private-link form
     * ("744275701/9a8b7c6d5e"), where the second part is the hash that an unlisted video needs
     * as `h=`. That exists because 7 of these 8 videos currently answer 401 to any embed - their
     * privacy settings forbid it - and the fix for those is either a Vimeo setting or a hash,
     * neither of which should require touching this file. See the snippet's comment.
     */
    var parts = String(videoId).split('/');
    var iframe = document.createElement('iframe');
    /*
     * `muted=0` is deliberate: these are customer testimonials and they are meant to start with
     * sound. Unmuted autoplay is only permitted because this modal can ONLY appear from a click
     * on a slide, which gives the document sticky user activation, and `allow="autoplay"`
     * delegates that to the cross-origin player. If a future change ever opens this modal
     * without a user gesture - on scroll, on a timer, on page load - the browser will refuse to
     * start it at all, and the fix is not to add `muted=1` here but to keep the gesture.
     *
     * dnt=1 keeps Vimeo from setting tracking cookies for a video the customer only watches.
     */
    iframe.src = 'https://player.vimeo.com/video/' + parts[0] +
      (parts[1] ? '?h=' + encodeURIComponent(parts[1]) + '&' : '?') +
      'autoplay=1&muted=0&title=0&byline=0&portrait=0&playsinline=1&dnt=1';
    iframe.setAttribute('allow', 'autoplay; fullscreen; picture-in-picture');
    iframe.setAttribute('allowfullscreen', '');
    iframe.setAttribute('title', 'Customer video');
    iframe.style.cssText = 'width:100%;height:100%;border:0;display:block';

    var shut = document.createElement('button');
    shut.type = 'button';
    shut.setAttribute('aria-label', 'Close video');
    shut.style.cssText = [
      'position:absolute', 'top:8px', 'right:8px', 'width:30px', 'height:30px',
      'z-index:1', 'border:0', 'padding:0', 'cursor:pointer', 'border-radius:50%',
      'background:rgba(0,0,0,0.55)', 'color:#fff', 'font:600 17px/30px system-ui, sans-serif'
    ].join(';');
    shut.textContent = '×';
    shut.addEventListener('click', function (e) { e.stopPropagation(); close(); });

    frame.appendChild(iframe);
    frame.appendChild(shut);
    overlay.appendChild(frame);

    // backdrop only: a click that started inside the player must not close it
    overlay.addEventListener('click', function (e) { if (e.target === overlay) close(); });

    var previousOverflow = document.body.style.overflow;
    document.body.appendChild(overlay);
    document.documentElement.classList.add(OPEN_CLASS);
    document.body.style.overflow = 'hidden';

    open = { overlay: overlay, opener: opener, previousOverflow: previousOverflow };
    shut.focus();
  }

  function bind(root) {
    var cfg = data();
    if (!cfg || !cfg.slides) return;

    Object.keys(cfg.slides).forEach(function (id) {
      var video = cfg.slides[id];
      var nodes = root.querySelectorAll('[data-component-id="' + id + '"]');
      [].forEach.call(nodes, function (node) {
        if (node.dataset.reploVideoModalBound === 'true') return;
        node.dataset.reploVideoModalBound = 'true';

        // it behaves like a button, so it has to say so
        if (!node.getAttribute('role')) node.setAttribute('role', 'button');
        if (!node.hasAttribute('tabindex')) node.setAttribute('tabindex', '0');
        node.style.cursor = 'pointer';

        node.addEventListener('click', function () { show(video, node); });
        node.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); show(video, node); }
        });
      });
    });
  }

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') close();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { bind(document); });
  } else {
    bind(document);
  }

  document.addEventListener('shopify:section:load', function (e) { bind(e.target); });
})();
